import { optionsAuth, CachedTokenContext, useInvalidationWrapper } from '@seabrookstudios/auth';
import { useQuery, useQueryClient, useMutation, useQueries } from '@tanstack/react-query';
import { useContext } from 'react';
import { alphabetically } from '@seabrookstudios/types';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { formatCurrency, byStatus, royaltyToYear } from '@seabrookstudios/pitch2table-core';

/**
 *
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns
 */
const hasToken = (token) => token !== '';

/**
 * @param {number} retryCount
 */
const exponentialBackoff = (retryCount) => {
  return 2 ** retryCount * 1000 + Math.floor(Math.random() * 1000);
};

axiosRetry(axios, { retries: 5, retryDelay: exponentialBackoff });

/**
 * @param {import('axios').AxiosRequestConfig} options,
 * @param {('v1'|'v2'|'v3')} version
 * @returns {import('axios').AxiosRequestConfig}
 */
const optionsVersion = (options, version) => {
  return {
    ...options,
    headers: {
      ...options.headers,
      version,
    },
  };
};

/**
 * @param {string} url
 * @param {import('axios').AxiosRequestConfig} config
 */
const get = async (url, config) => {

  try {
    const response = await axios.get(url, config);
    return response;
  } catch (error) {
    if (error.response.status === 401) {
      throw new Error('UNAUTHORISED');
    }

    throw error;
  }
};

/**
 * @param {string} url
 * @param {object} data
 * @param {import('axios').AxiosRequestConfig} config
 */
const post = async (url, data, config) => {
  try {
    const response = await axios.post(url, data, config);
    return response;
  } catch (error) {
    if (error.response.status === 401) {
      throw new Error('UNAUTHORISED');
    }

    throw error;
  }
};

/**
 * @param {string} url
 * @param {import('axios').AxiosRequestConfig} config
 */
const httpDelete = async (url, config) => {
  try {
    const response = await axios.delete(url, config);
    return response;
  } catch (error) {
    if (error.response.status === 401) {
      throw new Error('UNAUTHORISED');
    }

    throw error;
  }
};

const contactBook$1 = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    publishers: () => `${SecureApi}/contact-book/publishers`,
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
     */
    publisher: (publisherId) => ({
      relationship: () => `${SecureApi}/contact-book/publishers/${publisherId}`,
      submissions: () => `${SecureApi}/contact-book/publishers/${publisherId}/submissions`,
      documents: () => `${SecureApi}/contact-book/publishers/${publisherId}/documents`,
      /**
       * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
       */
      document: (attachmentId) => {
        return `${SecureApi}/contact-book/publishers/${publisherId}/documents/${attachmentId}`;
      },
      contacts: () => `${SecureApi}/contact-book/publishers/${publisherId}/contacts`,
    }),
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherForApp[]>}
 */
const fetchMyPublishers = async (token) => {
  const url = contactBook$1().publishers();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView[]>}
 */
const fetchMySubmissionsToPublisher = async (token, publisherId) => {
  const url = contactBook$1().publisher(publisherId).submissions();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').NonGameContract[]>}
 */
const fetchMyDocumentsRelatingToPublisher = async (token, publisherId) => {
  const url = contactBook$1().publisher(publisherId).documents();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PrivateContact[]>}
 */
const fetchMyContactsRelatingToPublisher = async (token, publisherId) => {
  const url = contactBook$1().publisher(publisherId).documents();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherRelationship>}
 */
const fetchMyDesignerPublisherRelationship = async (token, publisherId) => {
  const url = contactBook$1().publisher(publisherId).relationship();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 * @returns {Promise<import('@seabrookstudios/types').Url>}
 */
const fetchNonGameContract = async (token, publisherId, attachmentId) => {
  const url = contactBook$1().publisher(publisherId).document(attachmentId);

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 * @param {import('@seabrookstudios/pitch2table-core').EditableContract} body
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').NonGameContract>}
 */
const updateNonGameContract = async (token, publisherId, attachmentId, body) => {
  const url = contactBook$1().publisher(publisherId).document(attachmentId);

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

const contactBook = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    documents: () => `${SecureApi}/documents`,
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').DocumentListEntry[]>}
 */
const fetchMyDocuments = async (token) => {
  const url = contactBook().documents();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

const OneMinute$9 = 1000 * 60;
const OneHour$9 = OneMinute$9 * 60;
const OneDay$9 = OneHour$9 * 24;

const MyDocuments = ['my-documents'];
const MyDocument = (attachmentId) => ['my-documents', attachmentId];

/**
 * Returns all the publishers the designer has some relationship with. At the moment
 * this is scoped to pitches but will include custom data in the future
 */
const useMyDocuments = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyDocuments,
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').DocumentListEntry[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').DocumentListEntry[]}
       */
      const data = await wrapper(() => fetchMyDocuments(token));

      data.sort((a, b) => (a.signedDate || a.created) - (b.signedDate || b.created));

      return data;
    },
    staleTime: OneDay$9,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 */
const useDocument = (attachmentId) => {
  const { data: documents = [] } = useMyDocuments();

  return useQuery({
    queryKey: MyDocument(attachmentId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').DocumentListEntry|null>}
     */
    queryFn: async () => {
      const document = documents.find((d) => d.id === attachmentId);
      return document || null;
    },
    enabled: documents.length > 0,
  });
};

const OneMinute$8 = 1000 * 60;
const OneHour$8 = OneMinute$8 * 60;
const OneDay$8 = OneHour$8 * 24;

const MyPublishers = ['my-publishers'];
const MyPublisherSubmissions = (publisherId) => ['my-publishers-submissions', publisherId];
const MyPublisherDocuments = (publisherId) => ['my-publishers-documents', publisherId];
const MyPublisherContacts = (publisherId) => ['my-publishers-contacts', publisherId];
const MyDesignerPublisherRelationship = (publisherId) => [
  'my-publishers-relationship',
  publisherId,
];

/**
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId|undefined|null} attachmentId
 */
const NonGameContractUrls = (attachmentId) => ['non-game-contract-urls', attachmentId];

/**
 * Returns all the publishers the designer has some relationship with. At the moment
 * this is scoped to pitches but will include custom data in the future
 */
const useMyPublishers = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyPublishers,
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').ContactBookPublisher[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').ContactBookPublisher[]}
       */
      const data = await wrapper(() => fetchMyPublishers(token));

      data.sort(alphabetically('name'));

      return data;
    },
    staleTime: OneDay$8,
    enabled: hasToken(token),
  });
};

/**
 * Returns all the the submissions this designer has made to this publisher
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 */
const useMySubmissionsToPublisher = (publisherId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyPublisherSubmissions(publisherId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView[]}
       */
      const data = await wrapper(() => fetchMySubmissionsToPublisher(token, publisherId));

      return data;
    },
    staleTime: OneDay$8,
    enabled: hasToken(token),
  });
};

/**
 * Returns all the the documents this designer has uploaded relating to this publisher
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 */
const useMyDocumentsRelatingToPublisher = (publisherId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyPublisherDocuments(publisherId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').NonGameContract[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').NonGameContract[]}
       */
      const data = await wrapper(() => fetchMyDocumentsRelatingToPublisher(token, publisherId));

      return data;
    },
    staleTime: OneDay$8,
    enabled: hasToken(token),
  });
};

/**
 * Returns all the the contacts this designer has saved relating to this publisher
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 */
const useMyContactsRelatingToPublisher = (publisherId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyPublisherContacts(publisherId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PrivateContact[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').PrivateContact[]}
       */
      const data = await wrapper(() => fetchMyContactsRelatingToPublisher(token, publisherId));

      return data;
    },
    staleTime: OneDay$8,
    enabled: hasToken(token),
  });
};

/**
 * Returns all the the submissions this designer has made to this publisher
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 */
const useMyDesignerPublisherRelationship = (publisherId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyDesignerPublisherRelationship(publisherId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherRelationship>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').DesignerPublisherRelationship}
       */
      const data = await wrapper(() => fetchMyDesignerPublisherRelationship(token, publisherId));

      return data;
    },
    staleTime: OneDay$8,
    enabled: hasToken(token),
  });
};

/**
 * Returns a single contract
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 */
const useNonGameContract = (publisherId, attachmentId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: NonGameContractUrls(attachmentId),
    /**
     * @returns {Promise<import('@seabrookstudios/types').Url|null>}
     */
    queryFn: async () => {
      if (!publisherId) {
        return null;
      }
      if (!attachmentId) {
        return null;
      }

      /**
       * @type {import('@seabrookstudios/types').Url}
       */
      const data = await wrapper(() => fetchNonGameContract(token, publisherId, attachmentId));
      return data;
    },
    staleTime: OneDay$8,
    enabled: hasToken(token) && !!publisherId && !!attachmentId,
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateNonGameContract = (publisherId, attachmentId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').EditableContract} body
     */
    mutationFn: async (body) => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').NonGameContract}
       */
      const data = await wrapper(() =>
        updateNonGameContract(token, publisherId, attachmentId, body)
      );
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').NonGameContract} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: MyPublisherDocuments(publisherId) });
      queryClient.invalidateQueries({ queryKey: MyDocuments });
      queryClient.invalidateQueries({ queryKey: MyDocument(attachmentId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const users = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    me: () => {
      return `${SecureApi}/users/me`;
    },
    seed: () => {
      return `${SecureApi}/v1/users/seed`;
    },
    avatar: () => {
      return `${SecureApi}/v1/users/me/avatar`;
    },
  };
};

const contacts = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    /**
     * @param {import('@seabrookstudios/types').UserId} userId
     */
    get: (userId) => {
      return `${SecureApi}/v1/contacts/${userId}`;
    },
    search: () => {
      return `${SecureApi}/v1/users/search`;
    },
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').AdminOnlyPublicContact>}
 */
const fetchThisUser = async (token) => {
  const url = users().me();

  const response = await get(url, optionsVersion(optionsAuth(token), 'v2'));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').UserId} userId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublicContact>}
 */
const fetchContact = async (token, userId) => {
  const url = contacts().get(userId);

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {string} email
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublicContact|null>}
 */
const fetchContactByEmail = async (token, email) => {
  const url = contacts().search();

  const response = await post(url, { email }, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').UpdateUserRequest} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').AdminOnlyPublicContact>}
 */
const updateMyDetails = async (token, request) => {
  const url = users().me();

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

/**
 * @param {import("@seabrookstudios/auth").TokenString} token
 * @param {import("@seabrookstudios/pitch2table-core").Currency} currency
 * @returns {Promise<>}
 */
const updatePreferredCurrency = async (token, currency) => {
  return updateMyDetails(token, { preferredCurrency: currency });
};

const OneMinute$7 = 1000 * 60;
const OneHour$7 = OneMinute$7 * 60;
const OneDay$7 = OneHour$7 * 24;
const OneWeek = OneDay$7 * 7;

const MyProfile = ['my-profile'];

const useMyProfile = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyProfile,
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').AdminOnlyPublicContact>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').AdminOnlyPublicContact}
       */
      const data = await wrapper(() => fetchThisUser(token));
      return data;
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').UserId|undefined|null} userId
 */
const useContact = (userId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: ['user-profile', userId],
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublicContact|null>}
     */
    queryFn: async () => {
      if (!userId) {
        return null;
      }

      const data = await wrapper(() => fetchContact(token, userId));
      return data;
    },
    staleTime: OneWeek,
    enabled: hasToken(token) && !!userId,
  });
};

/**
 * @param {import('@seabrookstudios/types').UserId[]} userIds
 */
const useContacts = (userIds) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQueries({
    queries: userIds.map((userId) => {
      return {
        queryKey: ['user-profile', userId],
        /**
         * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublicContact>}
         */
        queryFn: async () => {
          const data = await wrapper(() => fetchContact(token, userId));
          return data;
        },
        staleTime: OneWeek,
        enabled: hasToken(token),
      };
    }),
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter((result) => !!result),
        pending: results.some((result) => result.isPending),
      };
    },
  });
};

/**
 * @param {string|undefined} email
 */
const useFindByEmail = (email) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  /**
   * @param {string|undefined} email
   */
  const hasValidEmail = (email) => {
    if (!email) {
      return false;
    }
    if (email.length === 0) {
      return false;
    }
    if (!email.includes('@')) {
      return false;
    }

    return true;
  };

  return useQuery({
    queryKey: ['find-by-email', email],
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublicContact|null>}
     */
    queryFn: async () => {
      if (!hasValidEmail(email)) {
        return null;
      }
      if (!email) {
        return null;
      }

      const data = await wrapper(() => fetchContactByEmail(token, email));
      return data;
    },
    staleTime: 0,
    enabled: hasValidEmail(email) && hasToken(token),
  });
};

const useUserCurrencyFormatter = () => {
  const { data: profile } = useMyProfile();

  if (!profile || !profile.currencySettings || !profile.currencySettings.preferredCurrency) {
    return (amount) => `${amount}`;
  }

  return formatCurrency(profile.currencySettings.preferredCurrency);
};

/**
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateProfile = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/pitch2table-core').UpdateUserRequest} props.toUpdate
     */
    mutationFn: async ({ toUpdate }) => {
      const data = await wrapper(() => updateMyDetails(token, toUpdate));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').AdminOnlyPublicContact} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      queryClient.setQueryData(MyProfile, data);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */

const useUpdatePreferredCurrency = (onSuccess, onError) => {
  const token = useContext(CachedTokenContext);

  const mutation = useMutation({
    /**
     * @param {import("@seabrookstudios/pitch2table-core").Currency} newValue
     */
    mutationFn: async (newValue) => {
      const response = await updatePreferredCurrency(token, newValue);
      return response;
    },
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });

  return mutation;
};

const endpoints$5 = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    /**
     * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
     */
    contract: (attachmentId) => `${SecureApi}/v1/contracts/${attachmentId}`,
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    game: (gameId) => {
      return {
        /**
         * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
         */
        contract: (attachmentId) => `${SecureApi}/v1/games/${gameId}/contracts/${attachmentId}`,
        contracts: () => `${SecureApi}/v1/games/${gameId}/contracts`,
      };
    },
  };
};
/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Contract[]>}
 */
const fetchGameContracts = async (token, gameId) => {
  const url = endpoints$5().game(gameId).contracts();

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 * @returns {Promise<import('@seabrookstudios/types').Url>}
 */
const fetchGameContract = async (token, gameId, attachmentId) => {
  const url = endpoints$5().game(gameId).contract(attachmentId);

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 * @param {import('@seabrookstudios/pitch2table-core').EditableContract} body
 * @returns {Promise<import('@seabrookstudios/types').Url>}
 */
const updateContract = async (token, gameId, attachmentId, body) => {
  const url = endpoints$5().game(gameId).contract(attachmentId);

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

const OneMinute$6 = 1000 * 60;
const OneHour$6 = OneMinute$6 * 60;
const OneDay$6 = OneHour$6 * 24;

/**
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const GameContracts = (gameId) => ['game-contracts', gameId];

/**
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId|undefined|null} attachmentId
 */
const ContractUrls = (attachmentId) => ['contract-urls', attachmentId];

/**
 * Returns the list of contracts associated with a game.
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const useGameContracts = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: GameContracts(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Contract[]>}
     */
    queryFn: async () => {
      if (!gameId) {
        return [];
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').Contract[]}
       */
      const data = await wrapper(() => fetchGameContracts(token, gameId));
      data.sort((a, b) => b.uploadedTimestamp - a.uploadedTimestamp);
      return data;
    },
    staleTime: OneDay$6,
    enabled: hasToken(token) && !!gameId,
  });
};

/**
 * Returns a single contract
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 */
const useGameContract = (gameId, attachmentId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: ContractUrls(attachmentId),
    /**
     * @returns {Promise<import('@seabrookstudios/types').Url|null>}
     */
    queryFn: async () => {
      if (!gameId) {
        return null;
      }
      if (!attachmentId) {
        return null;
      }

      /**
       * @type {import('@seabrookstudios/types').Url}
       */
      const data = await wrapper(() => fetchGameContract(token, gameId, attachmentId));
      return data;
    },
    staleTime: OneDay$6,
    enabled: hasToken(token) && !!gameId && !!attachmentId,
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateContract = (gameId, attachmentId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').EditableContract} body
     */
    mutationFn: async (body) => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').Contract}
       */
      const data = await wrapper(() => updateContract(token, gameId, attachmentId, body));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').Contract} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: GameContracts(gameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const endpoints$4 = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    create: () => `${SecureApi}/v1/games`,
    my: () => `${SecureApi}/v1/games`,
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    game: (gameId) => {
      return {
        get: () => `${SecureApi}/v1/games/${gameId}`,
        publishers: () => `${SecureApi}/v1/games/${gameId}/publishers`,
        sellsheet: () => `${SecureApi}/v1/games/${gameId}/sellsheet`,
        sellsheets: () => `${SecureApi}/v1/games/${gameId}/sellsheets`,
        rules: () => `${SecureApi}/v1/games/${gameId}/rules`,
        /**
         * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} attachmentId
         */
        contract: (attachmentId) => `${SecureApi}/v1/games/${gameId}/contracts/${attachmentId}`,
        contracts: () => `${SecureApi}/v1/games/${gameId}/contracts`,
      };
    },
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').GameSummary[]>}
 */
const fetchMyGames = async (token) => {
  const url = endpoints$4().my();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Game>}
 */
const fetchGame = async (token, gameId) => {
  const url = endpoints$4().game(gameId).get();

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Sellsheet[]>}
 */
const fetchGameSellsheets = async (token, gameId) => {
  const url = endpoints$4().game(gameId).sellsheets();

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Rules[]>}
 */
const fetchGameRules = async (token, gameId) => {
  const url = endpoints$4().game(gameId).rules();

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @typedef {Object} GameAndSummary
 * @property {import('@seabrookstudios/pitch2table-core').Game} game
 * @property {import('@seabrookstudios/pitch2table-core').GameSummary} summary
 */

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {string} name
 * @param {string} tag
 * @param {import('@seabrookstudios/pitch2table-core').DesignerGameStatus} status
 * @returns {Promise<GameAndSummary>}
 */
const createGame = async (token, name, tag, status) => {
  const url = endpoints$4().create();

  /**
   * @type {import('@seabrookstudios/pitch2table-core').CreateGameRequest}
   */
  const body = {
    name,
    tag,
    status,
  };

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').UpdateGameRequest} body
 * @returns {Promise<GameAndSummary>}
 */
const updateGame = async (token, gameId, body) => {
  const url = endpoints$4().game(gameId).get();

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

const OneMinute$5 = 1000 * 60;
const OneHour$5 = OneMinute$5 * 60;
const OneDay$5 = OneHour$5 * 24;

const MyGames = ['my-games'];

/**
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const GameById = (gameId) => ['games', gameId];

/**
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const GameSellsheets = (gameId) => ['game-sellsheets', gameId];

/**
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const GameRules = (gameId) => ['game-rules', gameId];

/**
 * Returns all games that a designer has access to
 */
const useMyGames = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyGames,
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').GameSummary[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').GameSummary[]}
       */
      const data = await wrapper(() => fetchMyGames(token));

      data.sort(alphabetically('name'));

      return data;
    },
    staleTime: OneDay$5,
    enabled: hasToken(token),
  });
};

/**
 * Returns a game from a designer's perspective
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const useGame = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: GameById(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Game|null>}
     */
    queryFn: async () => {
      if (!gameId) {
        return null;
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').Game}
       */
      const data = await wrapper(() => fetchGame(token, gameId));
      return data;
    },
    staleTime: OneDay$5,
    enabled: hasToken(token) && !!gameId,
  });
};

/**
 * Returns the list of sellsheets associated with a game
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const useGameSellsheets = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: GameSellsheets(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Sellsheet[]>}
     */
    queryFn: async () => {
      if (!gameId) {
        return [];
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').Sellsheet[]}
       */
      const data = await wrapper(() => fetchGameSellsheets(token, gameId));
      data.sort((a, b) => b.uploadedTimestamp - a.uploadedTimestamp);
      return data;
    },
    staleTime: OneDay$5,
    enabled: hasToken(token) && !!gameId,
  });
};

/**
 * Returns the list of rules associated with a game.
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const useGameRules = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: GameRules(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Rules[]>}
     */
    queryFn: async () => {
      if (!gameId) {
        return [];
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').Rules[]}
       */
      const data = await wrapper(() => fetchGameRules(token, gameId));
      data.sort((a, b) => b.uploadedTimestamp - a.uploadedTimestamp);
      return data;
    },
    staleTime: OneDay$5,
    enabled: hasToken(token) && !!gameId,
  });
};

/**
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useSaveGame = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {string} props.name
     * @param {string} props.tag
     * @param {import('@seabrookstudios/pitch2table-core').DesignerGameStatus} props.status
     */
    mutationFn: async ({ name, tag, status }) => {
      const data = await wrapper(() => createGame(token, name, tag, status));
      return data;
    },
    /**
     * @param {import('./services').GameAndSummary} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').GameSummary[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current, data.summary];
        newData.sort(alphabetically('name'));
        return newData;
      };

      queryClient.setQueryData(MyGames, appendGameSummary);
      queryClient.setQueryData(GameById(data.game.id), data.game);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @deprecated
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateGame = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/types').GameId} props.gameId
     * @param {import('@seabrookstudios/pitch2table-core').UpdateGameRequest} props.toUpdate
     */
    mutationFn: async ({ gameId, toUpdate }) => {
      const data = await wrapper(() => updateGame(token, gameId, toUpdate));
      return data;
    },
    /**
     * @param {import('./services').GameAndSummary} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').GameSummary[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current.filter((c) => c.id !== data.summary.id), data.summary];
        newData.sort(alphabetically('name'));
        return newData;
      };

      queryClient.setQueryData(MyGames, appendGameSummary);
      queryClient.setQueryData(GameById(data.game.id), data.game);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId the game to run the mutation on
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateGameV2 = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdateGameRequest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => updateGame(token, gameId, request));
      return data;
    },
    /**
     * @param {import('./services').GameAndSummary} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').GameSummary[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current.filter((c) => c.id !== data.summary.id), data.summary];
        newData.sort(alphabetically('name'));
        return newData;
      };

      queryClient.setQueryData(MyGames, appendGameSummary);
      queryClient.setQueryData(GameById(data.game.id), data.game);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const endpoints$3 = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    game: (gameId) => {
      return {
        publishers: () => `${SecureApi}/v1/games/${gameId}/publishers`,
      };
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
     */
    publisherGame: (publisherGameId) => {
      return {
        infov1: () => `${SecureApi}/v1/publisherGame/${publisherGameId}`,
        info: () => `${SecureApi}/v2/publisherGame/${publisherGameId}`,
        record: () => `${SecureApi}/publisherGame/${publisherGameId}`,
        followUp: () => `${SecureApi}/v1/publisherGame/${publisherGameId}/follow-up`,
        as: () => ({
          publisher: () => `${SecureApi}/v1/publisherGame/${publisherGameId}/as/publisher`,
          designer: () => `${SecureApi}/v1/publisherGame/${publisherGameId}/as/designer`,
        }),
        designer: () => ({
          updates: () => `${SecureApi}/v1/publisherGame/${publisherGameId}/designer-updates`,
          updatesv2: () => `${SecureApi}/v2/publisherGame/${publisherGameId}/designer-updates`,
        }),
        /**
         * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdateId} publisherGameUpdateId
         */
        update: (publisherGameUpdateId) => ({
          info: () =>
            `${SecureApi}/v1/publisherGame/${publisherGameId}/updates/${publisherGameUpdateId}`,
        }),
      };
    },
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView[]>}
 */
const fetchGamePublishers = async (token, gameId) => {
  const url = endpoints$3().game(gameId).publishers();

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate[]>}
 */
const fetchPublisherGameUpdates = async (token, publisherGameId) => {
  const url = endpoints$3().publisherGame(publisherGameId).designer().updates();

  const response = await get(url, optionsAuth(token));
  return response.data || [];
};

/**
 * @deprecated
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGame>}
 */
const fetchPublisherGame = async (token, publisherGameId) => {
  const url = endpoints$3().publisherGame(publisherGameId).infov1();

  const response = await get(url, optionsAuth(token));
  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGame>}
 */
const fetchPublisherGameAsPublisher = async (token, publisherGameId) => {
  const url = endpoints$3().publisherGame(publisherGameId).as().publisher();

  const response = await get(url, optionsAuth(token));
  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView>}
 */
const fetchPublisherGameAsDesigner = async (token, publisherGameId) => {
  const url = endpoints$3().publisherGame(publisherGameId).as().designer();

  const response = await get(url, optionsAuth(token));
  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').AddPublisherGameUpdateRequest} body
 * @param {('v1'|'v2'|'v3')} version
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate>}
 */
const addPublisherGameUpdateV = async (token, publisherGameId, body, version) => {
  const url = endpoints$3().publisherGame(publisherGameId).designer().updatesv2();

  const headers = optionsVersion(optionsAuth(token), version);
  const response = await post(url, body, headers);
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdateId} publisherGameUpdateId
 * @param {import('@seabrookstudios/pitch2table-core').EditPublisherGameUpdateRequest} body
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate>}
 */
const updatePublisherGameUpdate = async (
  token,
  publisherGameId,
  publisherGameUpdateId,
  body
) => {
  const url = endpoints$3().publisherGame(publisherGameId).update(publisherGameUpdateId).info();

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').UpdatePublisherGameRequest} body
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate>}
 */
const updatePublisherGame = async (token, publisherGameId, body) => {
  const url = endpoints$3().publisherGame(publisherGameId).info();

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').UpdatePublisherGameFollowUpRequest} body
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate>}
 */
const updatePublisherGameFollowUp = async (token, publisherGameId, body) => {
  const url = endpoints$3().publisherGame(publisherGameId).followUp();

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate>}
 */
const deletePublisherGameFollowUp = async (token, publisherGameId) => {
  const url = endpoints$3().publisherGame(publisherGameId).followUp();

  const response = await httpDelete(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdateId} publisherGameUpdateId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate>}
 */
const deletePublisherGameUpdate = async (token, publisherGameId, publisherGameUpdateId) => {
  const url = endpoints$3().publisherGame(publisherGameId).update(publisherGameUpdateId).info();

  const response = await httpDelete(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate>}
 */
const deletePublisherGame = async (token, publisherGameId) => {
  const url = endpoints$3().publisherGame(publisherGameId).record();

  const response = await httpDelete(url, optionsAuth(token));
  return response.data;
};

const OneMinute$4 = 1000 * 60;
const OneHour$4 = OneMinute$4 * 60;
const OneDay$4 = OneHour$4 * 24;

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate} a
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate} b
 */
const updatesByTimestamp = (a, b) => b.timestamp - a.timestamp;

/**
 * @param {import('@seabrookstudios/types').GameId|null|undefined} gameId
 */
const DesignerPublisherGamesForGame = (gameId) => [
  'designer-publisher-game-for-game',
  gameId,
];

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 */
const PublisherGameKey = (publisherGameId) => ['publisher-game', publisherGameId];

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 */
const DesignerPublisherGameKey = (publisherGameId) => [
  'designer-publisher-game',
  publisherGameId,
];

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null} publisherGameId
 */
const PublisherGameUpdatesKey = (publisherGameId) => [
  'publisher-game-updates',
  publisherGameId,
];

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdateId|null} updateId
 */
const PublisherGameUpdate = (publisherGameId, updateId) => [
  'publisher-game-updates',
  publisherGameId,
  updateId,
];

/**
 * @param {import('@seabrookstudios/types').GameId|null|undefined} gameId
 */
const useGamePublishers = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: DesignerPublisherGamesForGame(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView[]>}
     */
    queryFn: async () => {
      if (!gameId) {
        return [];
      }

      const data = await wrapper(() => fetchGamePublishers(token, gameId));

      const toSort = [...data];
      toSort.sort(byStatus);

      return toSort;
    },
    staleTime: OneDay$4,
    enabled: hasToken(token) && !!gameId,
  });
};

/**
 * @deprecated
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 */
const usePublisherGame = (publisherGameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: PublisherGameKey(publisherGameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGame|null>}
     */
    queryFn: async () => {
      if (!publisherGameId) {
        return null;
      }

      const data = await wrapper(() => fetchPublisherGame(token, publisherGameId));
      return data;
    },
    enabled: hasToken(token) && !!publisherGameId,
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 */
const usePublisherGameAsPublisher = (publisherGameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: PublisherGameKey(publisherGameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGame|null>}
     */
    queryFn: async () => {
      if (!publisherGameId) {
        return null;
      }

      const data = await wrapper(() => fetchPublisherGameAsPublisher(token, publisherGameId));
      return data;
    },
    enabled: hasToken(token) && !!publisherGameId,
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 */
const usePublisherGameAsDesigner = (publisherGameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: DesignerPublisherGameKey(publisherGameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').DesignerPublisherGameView|null>}
     */
    queryFn: async () => {
      if (!publisherGameId) {
        return null;
      }

      const data = await wrapper(() => fetchPublisherGameAsDesigner(token, publisherGameId));
      return data;
    },
    enabled: hasToken(token) && !!publisherGameId,
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null} publisherGameId
 */
const usePublisherGameUpdates = (publisherGameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: PublisherGameUpdatesKey(publisherGameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate[]>}
     */
    queryFn: async () => {
      if (!publisherGameId) {
        return [];
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate[]|null}
       */
      const data = await wrapper(() => fetchPublisherGameUpdates(token, publisherGameId));
      if (!data) {
        return [];
      }

      data.sort(updatesByTimestamp);

      return data;
    },
    enabled: hasToken(token) && !!publisherGameId,
    initialData: [],
    placeholderData: [],
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdateId} updateId
 */
const usePublisherGameUpdate = (publisherGameId, updateId) => {
  const { data: updates = [] } = usePublisherGameUpdates(publisherGameId);

  return useQuery({
    queryKey: PublisherGameUpdate(publisherGameId, updateId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherGameUpdate|null>}
     */
    queryFn: async () => {
      if (!updateId) {
        return null;
      }
      if (!updates) {
        return null;
      }

      const data = updates.find((update) => update.id === updateId);
      return data || null;
    },
    enabled: !!updateId,
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdatePublisherGamev2 = (publisherGameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdatePublisherGameRequest} request
     */
    mutationFn: async (request) => {
      if (!publisherGameId) {
        return;
      }

      const data = await wrapper(() => updatePublisherGame(token, publisherGameId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGame} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGamesForGame(data.gameId) });
      await queryClient.invalidateQueries({ queryKey: PublisherGameKey(publisherGameId) });
      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGameKey(publisherGameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateFollowUp = (publisherGameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdatePublisherGameFollowUpRequest} request
     */
    mutationFn: async (request) => {
      if (!publisherGameId) {
        return;
      }

      const data = await wrapper(() =>
        updatePublisherGameFollowUp(token, publisherGameId, request)
      );
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGame} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGamesForGame(data.gameId) });
      await queryClient.invalidateQueries({ queryKey: PublisherGameKey(publisherGameId) });
      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGameKey(publisherGameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useDeleteFollowUp = (publisherGameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    mutationFn: async () => {
      if (!publisherGameId) {
        return;
      }

      const data = await wrapper(() => deletePublisherGameFollowUp(token, publisherGameId));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGame} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGamesForGame(data.gameId) });
      await queryClient.invalidateQueries({ queryKey: PublisherGameKey(publisherGameId) });
      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGameKey(publisherGameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId|null|undefined} publisherGameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useDeletePublisherGame = (gameId, publisherGameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    mutationFn: async () => {
      if (!publisherGameId) {
        return;
      }

      await wrapper(() => deletePublisherGame(token, publisherGameId));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGamesForGame(gameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @deprecated
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdatePublisherGame = (publisherGameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').AddPublisherGameUpdateRequest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() =>
        addPublisherGameUpdateV(token, publisherGameId, request, 'v1')
      );
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current.filter((c) => c.id !== data.id), data];
        newData.sort(updatesByTimestamp);
        return newData;
      };

      queryClient.setQueryData(PublisherGameUpdatesKey(publisherGameId), appendGameSummary);
      queryClient.invalidateQueries({ queryKey: PublisherGameUpdate(publisherGameId, data.id) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @deprecated
 */
const useAddPublisherGameUpdate = useUpdatePublisherGame;

/**
 * @deprecated
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useAddPublisherGameUpdateV2 = (publisherGameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').AddPublisherGameUpdateRequest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() =>
        addPublisherGameUpdateV(token, publisherGameId, request, 'v2')
      );
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current.filter((c) => c.id !== data.id), data];
        newData.sort(updatesByTimestamp);
        return newData;
      };

      queryClient.setQueryData(PublisherGameUpdatesKey(publisherGameId), appendGameSummary);
      queryClient.invalidateQueries({ queryKey: PublisherGameUpdate(publisherGameId, data.id) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {('v1'|'v2'|'v3')} [version]
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useAddPublisherGameUpdateV = (publisherGameId, version = 'v3', onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').AddPublisherGameUpdateRequest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() =>
        addPublisherGameUpdateV(token, publisherGameId, request, version)
      );
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current.filter((c) => c.id !== data.id), data];
        newData.sort(updatesByTimestamp);
        return newData;
      };

      queryClient.setQueryData(PublisherGameUpdatesKey(publisherGameId), appendGameSummary);
      await queryClient.invalidateQueries({
        queryKey: PublisherGameUpdate(publisherGameId, data.id),
      });
      // await queryClient.invalidateQueries({ queryKey: DesignerPublisherGamesForGame(data.gameId) });
      await queryClient.invalidateQueries({ queryKey: PublisherGameKey(publisherGameId) });
      await queryClient.invalidateQueries({ queryKey: DesignerPublisherGameKey(publisherGameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdateId} publisherGameUpdateId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdatePublisherGameUpdate = (
  publisherGameId,
  publisherGameUpdateId,
  onSuccess,
  onError
) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').EditPublisherGameUpdateRequest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() =>
        updatePublisherGameUpdate(token, publisherGameId, publisherGameUpdateId, request)
      );
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdate[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current.filter((c) => c.id !== data.id), data];
        newData.sort(updatesByTimestamp);
        return newData;
      };

      queryClient.setQueryData(PublisherGameUpdatesKey(publisherGameId), appendGameSummary);
      queryClient.invalidateQueries({ queryKey: PublisherGameUpdate(publisherGameId, data.id) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameId} publisherGameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherGameUpdateId} publisherGameUpdateId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useDeletePublisherGameUpdate = (
  publisherGameId,
  publisherGameUpdateId,
  onSuccess,
  onError
) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    mutationFn: async () => {
      await wrapper(() => deletePublisherGameUpdate(token, publisherGameId, publisherGameUpdateId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PublisherGameUpdatesKey(publisherGameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const endpoints$2 = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     * @param {import('@seabrookstudios/types').UserId} userId
     */
    codesigner: (gameId, userId) => `${SecureApi}/v1/games/${gameId}/designers/${userId}`,
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     * @param {import('@seabrookstudios/types').UserId} userId
     */
    invite: (gameId, userId) => `${SecureApi}/v1/games/${gameId}/designers/${userId}/invite`,
    /**
     * @param {import('@seabrookstudios/pitch2table-core').InviteId} inviteId
     */
    invitation: (inviteId) => `${SecureApi}/v1/invites/${inviteId}`,
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    invites: (gameId) => `${SecureApi}/v1/games/${gameId}/invites`,
    myInvites: () => `${SecureApi}/v1/invites`,
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/types').UserId} userId
 * @returns {Promise<void>}
 */
const inviteUserToGame = async (token, gameId, userId) => {
  const url = endpoints$2().invite(gameId, userId);

  const response = await post(url, {}, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/types').UserId} userId
 * @returns {Promise<void>}
 */
const removeUserFromGame = async (token, gameId, userId) => {
  const url = endpoints$2().codesigner(gameId, userId);

  const response = await httpDelete(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Invite[]>}
 */
const fetchInvitesForGame = async (token, gameId) => {
  const url = endpoints$2().invites(gameId);

  const response = await get(url, optionsAuth(token));

  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Invite[]>}
 */
const fetchInvitesForUser = async (token) => {
  const url = endpoints$2().myInvites();

  const response = await get(url, optionsAuth(token));

  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').InviteId} inviteId
 * @returns {Promise<void>}
 */
const deleteInvite = async (token, inviteId) => {
  const url = endpoints$2().invitation(inviteId);

  const response = await httpDelete(url, optionsAuth(token));
  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').InviteId} inviteId
 * @returns {Promise<void>}
 */
const acceptInvite = async (token, inviteId) => {
  const url = endpoints$2().invitation(inviteId);

  const response = await post(url, {}, optionsAuth(token));
  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').InviteId} inviteId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Game>}
 */
const fetchInvite = async (token, inviteId) => {
  const url = endpoints$2().invitation(inviteId);

  const response = await get(url, optionsAuth(token));
  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const InvitesForGame = (gameId) => ['game-invites', gameId];
const MyInvites = () => ['my-invites'];

/**
 * Returns the game at the other end of an invite
 * @param {import('@seabrookstudios/pitch2table-core').InviteId|undefined|null} inviteId
 */
const useInviteDetails = (inviteId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: ['invite', inviteId],
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Game|null>}
     */
    queryFn: async () => {
      if (!inviteId) {
        return null;
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').Game}
       */
      const data = await wrapper(() => fetchInvite(token, inviteId));
      return data;
    },
    enabled: hasToken(token) && !!inviteId,
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId|undefined|null} gameId
 */
const useInvitesForGame = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: InvitesForGame(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Invite[]>}
     */
    queryFn: async () => {
      if (!gameId) {
        return [];
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').Invite[]}
       */
      const data = await wrapper(() => fetchInvitesForGame(token, gameId));
      return data || [];
    },
    placeholderData: [],
    staleTime: 10000,
    enabled: hasToken(token) && !!gameId,
  });
};

const useInvitesForUser = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyInvites(),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Invite[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').Invite[]}
       */
      const data = await wrapper(() => fetchInvitesForUser(token));
      return data || [];
    },
    placeholderData: [],
    staleTime: 10000,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useInviteUser = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/types').UserId} props.userId
     */
    mutationFn: async ({ userId }) => {
      await wrapper(() => inviteUserToGame(token, gameId, userId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: InvitesForGame(gameId) });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useRemoveUser = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/types').UserId} props.userId
     */
    mutationFn: async ({ userId }) => {
      await wrapper(() => removeUserFromGame(token, gameId, userId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MyGames });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useWithdrawInvite = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/pitch2table-core').InviteId} props.inviteId
     */
    mutationFn: async ({ inviteId }) => {
      await wrapper(() => deleteInvite(token, inviteId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: InvitesForGame(gameId) });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useAcceptInvite = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/pitch2table-core').InviteId} props.inviteId
     */
    mutationFn: async ({ inviteId }) => {
      await wrapper(() => acceptInvite(token, inviteId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MyInvites() });
      queryClient.invalidateQueries({ queryKey: MyGames });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useDeclineInvite = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/pitch2table-core').InviteId} props.inviteId
     */
    mutationFn: async ({ inviteId }) => {
      await wrapper(() => deleteInvite(token, inviteId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MyInvites() });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const endpoints$1 = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    create: () => `${SecureApi}/v1/unlinked-publishers`,
    myUnlinked: () => `${SecureApi}/v1/unlinked-publishers`,
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
     */
    publisher: (publisherId) => {
      return {
        info: () => `${SecureApi}/v1/publishers/${publisherId}/info`,
        /**
         * @param {import('@seabrookstudios/types').GameId} gameId
         */
        game: (gameId) => {
          return {
            get: () => `${SecureApi}/v1/unlinked-publishers/${publisherId}/games/${gameId}`,
          };
        },
      };
    },
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    game: (gameId) => {
      return {
        publishers: () => `${SecureApi}/v1/games/${gameId}/publishers`,
      };
    },
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherId[]>}
 */
const fetchMyUnlinkedPublishers = async (token) => {
  const url = endpoints$1().myUnlinked();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {string} name
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PublisherId>}
 */
const createUnlinkedPublisher = async (token, name) => {
  const url = endpoints$1().create();

  /**
   * @type {import('@seabrookstudios/pitch2table-core').AddOffPlatformPublisherRequest}
   */
  const body = {
    name,
  };

  const response = await post(url, body, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @returns {Promise<void>}
 */
const addGameToPublisher = async (token, gameId, publisherId) => {
  const url = endpoints$1().publisher(publisherId).game(gameId).get();

  const response = await post(url, {}, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId[]} publisherIds
 * @returns {Promise<void>}
 */
const shortlistPublishers = async (token, gameId, publisherIds) => {
  const url = endpoints$1().game(gameId).publishers();

  const response = await post(url, { publisherIds }, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 * @returns {Promise<[import('@seabrookstudios/pitch2table-core').PublisherForApp, import('@seabrookstudios/pitch2table-core').PublisherContact[]]>}
 */
const fetchPublisherAndContacts = async (token, publisherId) => {
  const url = endpoints$1().publisher(publisherId).info();

  const response = await get(url, optionsAuth(token));
  return response.data;
};

const OneMinute$3 = 1000 * 60;
const OneHour$3 = OneMinute$3 * 60;
const OneDay$3 = OneHour$3 * 24;

const MyUnlinkedPublishers = () => ['my-unlinked-publishers'];

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId|null|undefined} publisherId
 */
const MyUnlinkedPublisher = (publisherId) => ['my-unlinked-publishers', publisherId];

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId|null|undefined} publisherId
 */
const Publisher = (publisherId) => ['publishers', publisherId];

const useMyUnlinkedPublishers = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyUnlinkedPublishers(),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Publisher[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').Publisher[]}
       */
      const data = await wrapper(() => fetchMyUnlinkedPublishers(token));

      data.sort(alphabetically('name'));

      return data;
    },
    staleTime: OneDay$3,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId|null|undefined} publisherId
 */
const useUnlinkedPublisher = (publisherId) => {
  const { data: publishers } = useMyUnlinkedPublishers();

  return useQuery({
    queryKey: MyUnlinkedPublisher(publisherId),
    /**
     * @returns {import('@seabrookstudios/pitch2table-core').Publisher?}
     */
    queryFn: () => {
      if (!publisherId) {
        return null;
      }
      if (!publishers) {
        return null;
      }

      return publishers.find((pub) => pub.id === publisherId) || null;
    },
    staleTime: OneDay$3,
    enabled: !!publisherId,
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId} publisherId
 */
const usePublisherAndContacts = (publisherId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: Publisher(publisherId),
    /**
     * @returns {Promise<[import('@seabrookstudios/pitch2table-core').PublisherForApp|null, import('@seabrookstudios/pitch2table-core').PublicContact[]]>}
     */
    queryFn: async () => {
      const data = await wrapper(() => fetchPublisherAndContacts(token, publisherId));
      if (!data) {
        return [null, []];
      }

      return data;
    },
    staleTime: 0,
  });
};

/**
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useCreateUnlinkedPublisher = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {string} props.name
     */
    mutationFn: async ({ name }) => {
      const data = await wrapper(() => createUnlinkedPublisher(token, name));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').Publisher} data
     */
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').Publisher[]} current
       */
      const appendGameSummary = (current) => {
        const newData = [...current, data];
        newData.sort(alphabetically('name'));
        return newData;
      };

      queryClient.setQueryData(MyUnlinkedPublishers(), appendGameSummary);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useAddGameToPublisher = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/pitch2table-core').PublisherId} props.publisherId
     */
    mutationFn: async ({ publisherId }) => {
      const data = await wrapper(() => addGameToPublisher(token, gameId, publisherId));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DesignerPublisherGamesForGame(gameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useShortlistPublisher = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {object} props
     * @param {import('@seabrookstudios/pitch2table-core').PublisherId[]} props.publisherIds
     */
    mutationFn: async ({ publisherIds }) => {
      const data = await wrapper(() => shortlistPublishers(token, gameId, publisherIds));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DesignerPublisherGamesForGame(gameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const endpoints = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    royalties: () => `${SecureApi}/royalties`,
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    game: (gameId) => ({
      royalties: () => `${SecureApi}/games/${gameId}/royalties`,
      /**
       * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventId} royaltyId
       */
      royalty: (royaltyId) => `${SecureApi}/games/${gameId}/royalties/${royaltyId}`,
    }),
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp[]>}
 */
const fetchAllRoyalties = async (token) => {
  const url = endpoints().royalties();

  const response = await get(url, optionsAuth(token));

  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp[]>}
 */
const fetchRoyaltiesForGame = async (token, gameId) => {
  const url = endpoints().game(gameId).royalties();

  const response = await get(url, optionsAuth(token));

  return response.data || [];
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').CreateRoyaltyEvent} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp>}
 */
const createRoyaltyEvent = async (token, gameId, request) => {
  const url = endpoints().game(gameId).royalties();

  const response = await post(url, request, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventId} royaltyId
 * @param {import('@seabrookstudios/pitch2table-core').UpdateRoyaltyEvent} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp>}
 */
const updateRoyaltyEvent = async (token, gameId, royaltyId, request) => {
  const url = endpoints().game(gameId).royalty(royaltyId);

  const response = await post(url, request, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventId} royaltyId
 * @returns {Promise<void>}
 */
const deleteRoyaltyEvent = async (token, gameId, royaltyId) => {
  const url = endpoints().game(gameId).royalty(royaltyId);

  await httpDelete(url, optionsAuth(token));
};

const OneMinute$2 = 1000 * 60;
const OneHour$2 = OneMinute$2 * 60;
const OneDay$2 = OneHour$2 * 24;

const AllRoyalties = () => ['game-royalties'];
const RoyaltiesByGame = () => ['game-royalties', 'royalties-by-game'];
const RoyaltiesByYear = () => ['game-royalties', 'royalties-by-year'];
const RoyaltiesByPublisher = () => ['game-royalties', 'royalties-by-publisher'];
const RoyaltiesByType = () => ['game-royalties', 'royalties-by-type'];
/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const RoyaltiesForGame = (gameId) => ['game-royalties', 'royalties-by-game', gameId];
/**
 * @param {string|undefined} year
 */
const RoyaltiesForYear = (year) => ['game-royalties', 'royalties-by-year', year];
/**
 * @param {import('@seabrookstudios/pitch2table-core').PublisherId|"unlinked"} publisherId
 */
const RoyaltiesForPublisher = (publisherId) => [
  'game-royalties',
  'royalties-by-publisher',
  publisherId,
];
/**
 * @param {import('@seabrookstudios/pitch2table-core').RoyaltyType|undefined} type
 */
const RoyaltiesForType = (type) => ['game-royalties', 'royalties-by-type', type];

const useAllRoyalties = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: AllRoyalties(),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp[]}
       */
      const data = await wrapper(() => fetchAllRoyalties(token));
      return data || [];
    },
    placeholderData: [],
    staleTime: OneDay$2,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId|null|undefined} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useCreateRoyaltyEvent = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').CreateRoyaltyEvent} request
     */
    mutationFn: async (request) => {
      if (!gameId) {
        return;
      }

      const data = await wrapper(() => createRoyaltyEvent(token, gameId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: AllRoyalties() });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId|null|undefined} gameId
 * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventId|null|undefined} royaltyId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateRoyaltyEvent = (gameId, royaltyId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdateRoyaltyEvent} request
     */
    mutationFn: async (request) => {
      if (!gameId || !royaltyId) {
        return;
      }

      const data = await wrapper(() => updateRoyaltyEvent(token, gameId, royaltyId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: AllRoyalties() });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId|null|undefined} gameId
 * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventId|null|undefined} royaltyId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useDeleteRoyaltyEvent = (gameId, royaltyId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    mutationFn: async () => {
      if (!gameId || !royaltyId) {
        return;
      }

      await wrapper(() => deleteRoyaltyEvent(token, gameId, royaltyId));
    },
    onSuccess: async () => {
      if (!gameId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: AllRoyalties() });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const useAllRoyaltiesByGame = () => {
  const { data: royalties = [] } = useAllRoyalties();

  return useQuery({
    queryKey: RoyaltiesByGame(),
    /**
     * @returns {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[][]}
     */
    queryFn: () => {
      const gameIds = royalties
        .map((r) => r.gameId)
        .reduce(
          (acc, v) => (acc.includes(v) ? acc : [...acc, v]),
          /** @type {import("@seabrookstudios/types").GameId[]} */ ([])
        );

      return gameIds.map((gameId) => royalties.filter((r) => r.gameId === gameId));
    },
    staleTime: 0,
  });
};

const useAllRoyaltiesByPublisher = () => {
  const { data: royalties = [] } = useAllRoyalties();

  return useQuery({
    queryKey: RoyaltiesByPublisher(),
    /**
     * @returns {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[][]}
     */
    queryFn: () => {
      const gameIds = royalties
        .map((r) => r.publisherId)
        .reduce(
          (acc, v) => (acc.includes(v) ? acc : [...acc, v]),
          /** @type {(import("@seabrookstudios/pitch2table-core").PublisherId|undefined)[]} */ ([])
        );

      return gameIds.map((publisherId) => royalties.filter((r) => r.publisherId === publisherId));
    },
    staleTime: 0,
  });
};
const useAllRoyaltiesByType = () => {
  const { data: royalties = [] } = useAllRoyalties();

  return useQuery({
    queryKey: RoyaltiesByType(),
    /**
     * @returns {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[][]}
     */
    queryFn: () => {
      const gameIds = royalties
        .map((r) => r.type)
        .reduce(
          (acc, v) => (acc.includes(v) ? acc : [...acc, v]),
          /** @type {import("@seabrookstudios/pitch2table-core").RoyaltyType[]} */ ([])
        );

      return gameIds.map((type) => royalties.filter((r) => r.type === type));
    },
    staleTime: 0,
  });
};

const useAllRoyaltiesByYear = () => {
  const { data: royalties = [] } = useAllRoyalties();

  return useQuery({
    queryKey: RoyaltiesByYear(),
    /**
     * @returns {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[][]}
     */
    queryFn: () => {
      const years = royalties
        .map(royaltyToYear)
        .reduce((acc, v) => (acc.includes(v) ? acc : [...acc, v]), []);

      const byYear = years.map((year) => royalties.filter((r) => royaltyToYear(r) === year));

      byYear.sort((a, b) => royaltyToYear(b[0]) - royaltyToYear(a[0]));

      return byYear;
    },
    staleTime: 0,
  });
};

/**
 * @param {string|undefined} year
 */
const useAllRoyaltiesForYear = (year) => {
  const { data: royalties = [] } = useAllRoyalties();

  return useQuery({
    queryKey: RoyaltiesForYear(year),
    /**
     * @returns {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[]}
     */
    queryFn: () => {
      return royalties.filter((r) => royaltyToYear(r) === year);
    },
    staleTime: 0,
    placeholderData: [],
    enabled: year !== undefined,
  });
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyType|undefined} type
 */
const useAllRoyaltiesForType = (type) => {
  const { data: royalties = [] } = useAllRoyalties();

  return useQuery({
    queryKey: RoyaltiesForType(type),
    /**
     * @returns {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[]}
     */
    queryFn: () => {
      return royalties.filter((r) => r.type === type);
    },
    staleTime: 0,
    placeholderData: [],
    enabled: type !== undefined,
  });
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId|"unlinked"} publisherId
 */
const useAllRoyaltiesForPublisher = (publisherId) => {
  const { data: royalties = [] } = useAllRoyalties();

  return useQuery({
    queryKey: RoyaltiesForPublisher(publisherId),
    /**
     * @returns {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[]}
     */
    queryFn: () => {
      return royalties.filter(
        (r) => r.publisherId === (publisherId === 'unlinked' ? undefined : publisherId)
      );
    },
    staleTime: 0,
    placeholderData: [],
    enabled: publisherId !== undefined,
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const useGameRoyalties = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: RoyaltiesForGame(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp[]>}
     */
    queryFn: async () => {
      if (!gameId) {
        return [];
      }

      /**
       * @type {import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp[]}
       */
      const data = await wrapper(() => fetchRoyaltiesForGame(token, gameId));
      return data || [];
    },
    placeholderData: [],
    staleTime: OneDay$2,
    enabled: hasToken(token) && !!gameId,
  });
};

const messageBoard = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    game: (gameId) => ({
      /**
       * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
       */
      topic: (topicId) => `${SecureApi}/games/${gameId}/topics/${topicId}`,
      topics: () => `${SecureApi}/games/${gameId}/topics`,
    }),
    /**
     * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
     */
    topic: (topicId) => ({
      /**
       * @param {import('@seabrookstudios/pitch2table-core').MessageId} messageId
       */
      message: (messageId) => `${SecureApi}/topics/${topicId}/messages/${messageId}`,
      messages: () => `${SecureApi}/topics/${topicId}/messages`,
    }),
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Topic[]>}
 */
const fetchGameTopics = async (token, gameId) => {
  const url = messageBoard().game(gameId).topics();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').CreateTopicEvent} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Topic>}
 */
const createGameTopic = async (token, gameId, request) => {
  const url = messageBoard().game(gameId).topics();

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 * @param {import('@seabrookstudios/pitch2table-core').UpdateTopicEvent} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Topic>}
 */
const updateGameTopic = async (token, gameId, topicId, request) => {
  const url = messageBoard().game(gameId).topic(topicId);

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Message[]>}
 */
const fetchTopicMessages = async (token, topicId) => {
  const url = messageBoard().topic(topicId).messages();

  const response = await get(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 * @param {import('@seabrookstudios/pitch2table-core').CreateTopicMessageEvent} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Message>}
 */
const createTopicMessage = async (token, topicId, request) => {
  const url = messageBoard().topic(topicId).messages();

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 * @param {import('@seabrookstudios/pitch2table-core').MessageId} messageId
 * @param {import('@seabrookstudios/pitch2table-core').UpdateTopicMessageEvent} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').Message>}
 */
const updateTopicMessage = async (token, topicId, messageId, request) => {
  const url = messageBoard().topic(topicId).message(messageId);

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

const OneMinute$1 = 1000 * 60;
const OneHour$1 = OneMinute$1 * 60;
const OneDay$1 = OneHour$1 * 24;

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const GameTopicsCacheKey = (gameId) => ['game-topics', gameId];
/**
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 */
const TopicCacheKey = (topicId) => ['topic', topicId];
/**
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 */
const TopicMessagesCacheKey = (topicId) => ['messages', topicId];

/**
 * Returns all the topics for the game
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const useGameTopics = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: GameTopicsCacheKey(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Topic[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').Topic[]}
       */
      const data = await wrapper(() => fetchGameTopics(token, gameId));

      data.sort((a, b) => b.created - a.created);

      return data;
    },
    staleTime: OneDay$1,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 */
const useGameTopic = (gameId, topicId) => {
  const { data: topics = [] } = useGameTopics(gameId);

  return useQuery({
    queryKey: TopicCacheKey(topicId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Topic|null>}
     */
    queryFn: async () => {
      const topic = topics.find((d) => d.id === topicId);
      return topic || null;
    },
    staleTime: OneDay$1,
    enabled: topics.length > 0,
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 */
const useTopicMessages = (topicId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: TopicMessagesCacheKey(topicId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').Message[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').Message[]}
       */
      const data = await wrapper(() => fetchTopicMessages(token, topicId));
      return data;
    },
    staleTime: OneDay$1,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useCreateGameTopic = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').CreateTopicEvent} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => createGameTopic(token, gameId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').Topic} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: GameTopicsCacheKey(gameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId|null|undefined} gameId
 * @param {import('@seabrookstudios/pitch2table-core').TopicId|null|undefined} topicId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateGameTopic = (gameId, topicId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdateTopicEvent} request
     */
    mutationFn: async (request) => {
      if (!gameId || !topicId) {
        return;
      }

      const data = await wrapper(() => updateGameTopic(token, gameId, topicId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').RoyaltyEventForApp} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }
      if (!topicId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: GameTopicsCacheKey(gameId) });
      await queryClient.invalidateQueries({ queryKey: TopicCacheKey(topicId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').TopicId} topicId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useCreateTopicMessage = (topicId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').CreateTopicMessageEvent} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => createTopicMessage(token, topicId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').Message} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!topicId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: TopicMessagesCacheKey(topicId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/pitch2table-core').TopicId|null|undefined} topicId
 * @param {import('@seabrookstudios/pitch2table-core').MessageId|null|undefined} messageId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdateTopicMessage = (topicId, messageId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdateTopicMessageEvent} request
     */
    mutationFn: async (request) => {
      if (!topicId || !messageId) {
        return;
      }

      const data = await wrapper(() => updateTopicMessage(token, topicId, messageId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').Message} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!topicId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: TopicMessagesCacheKey(topicId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

const my = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    entitlements: () => `${SecureApi}/my/entitlements`,
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').UserEntitlements>}
 */
const fetchMyEntitlements = async (token) => {
  const url = my().entitlements();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

const MyEntitlements = ['my-entitlements'];

const useMyEntitlements = () => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: MyEntitlements,
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').UserEntitlements>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').UserEntitlements}
       */
      const data = await wrapper(() => fetchMyEntitlements(token));

      return data;
    },
    staleTime: 5000,
    enabled: hasToken(token),
  });
};

const urls = () => {
  const SecureApi = 'https://secure.pitch2table.com';

  return {
    /**
     * @param {import('@seabrookstudios/types').GameId} gameId
     */
    game: (gameId) => ({
      import: () => `${SecureApi}/games/${gameId}/import`,
      tests: () => `${SecureApi}/games/${gameId}/tests`,
      /**
       * @param {import('@seabrookstudios/pitch2table-core').PlayTestId} playTestId
       */
      test: (playTestId) => `${SecureApi}/games/${gameId}/tests/${playTestId}`,
      metaData: () => `${SecureApi}/games/${gameId}/metadata`,
      /**
       * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponentId} metaId
       */
      metaDataEntry: (metaId) => `${SecureApi}/games/${gameId}/metadata/${metaId}`,
    }),
  };
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTest[]>}
 */
const fetchGamePlayTests = async (token, gameId) => {
  const url = urls().game(gameId).tests();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').CreatePlayTest} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTest>}
 */
const createPlayTest = async (token, gameId, request) => {
  const url = urls().game(gameId).tests();

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};
/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').ImportPlayTest} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTest>}
 */
const importPlayTest = async (token, gameId, request) => {
  const url = urls().game(gameId).import();

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestId} playTestId
 * @param {import('@seabrookstudios/pitch2table-core').UpdatePlayTest} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTest>}
 */
const updatePlayTest = async (token, gameId, playTestId, request) => {
  const url = urls().game(gameId).test(playTestId);

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestId} playTestId
 * @returns {Promise<void>}
 */
const deletePlayTest = async (token, gameId, playTestId) => {
  const url = urls().game(gameId).test(playTestId);

  const response = await httpDelete(url, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent[]>}
 */
const fetchGamePlayTestMetaData = async (token, gameId) => {
  const url = urls().game(gameId).metaData();

  const response = await get(url, optionsAuth(token));

  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').CreatePlayTestMetadata} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent>}
 */
const createPlayTestMetaData = async (token, gameId, request) => {
  const url = urls().game(gameId).metaData();

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

/**
 * @param {import('@seabrookstudios/auth').TokenString} token
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponentId} metaId
 * @param {import('@seabrookstudios/pitch2table-core').UpdatePlayTestMetadata} request
 * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent>}
 */
const updatePlayTestMetaData = async (token, gameId, metaId, request) => {
  const url = urls().game(gameId).metaDataEntry(metaId);

  const response = await post(url, request, optionsAuth(token));
  return response.data;
};

const OneMinute = 1000 * 60;
const OneHour = OneMinute * 60;
const OneDay = OneHour * 24;

/**
 * @param {import('@seabrookstudios/pitch2table-core').PlayTest} a
 * @param {import('@seabrookstudios/pitch2table-core').PlayTest} b
 */
const ByDateOccurred = (a, b) => b.date - a.date;

/**
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent} a
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent} b
 */
const byOrder = (a, b) => a.order - b.order;

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const GamePlayTestsCacheKey = (gameId) => ['game-play-tests', gameId];
/**
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestId} playTestId
 */
const PlayTestCacheKey = (gameId, playTestId) => ['game-play-tests', gameId, playTestId];

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const GamePlayTestMetadataCacheKey = (gameId) => ['game-play-test-meta-data', gameId];
/**
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponentId} metaId
 */
const PlayTestMetaDataCacheKey = (gameId, metaId) => [
  'game-play-test-meta-data',
  gameId,
  metaId,
];

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const useGamePlayTests = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: GamePlayTestsCacheKey(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTest[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').PlayTest[]}
       */
      const data = await wrapper(() => fetchGamePlayTests(token, gameId));

      data.sort(ByDateOccurred);

      return data;
    },
    staleTime: OneDay,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestId} playTestId
 */
const useGamePlayTest = (gameId, playTestId) => {
  const { data: topics = [] } = useGamePlayTests(gameId);

  return useQuery({
    queryKey: PlayTestCacheKey(gameId, playTestId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTest|null>}
     */
    queryFn: async () => {
      const topic = topics.find((d) => d.id === playTestId);
      return topic || null;
    },
    staleTime: OneMinute,
    enabled: topics.length > 0,
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {(data: import('@seabrookstudios/pitch2table-core').PlayTest) => void} [onSuccess]
 * @param {() => void} [onError]
 */
const useCreatePlayTest = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').CreatePlayTest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => createPlayTest(token, gameId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PlayTest} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PlayTest[]} current
       */
      const appendPlayTest = (current) => {
        const newData = [...current, data];
        newData.sort(ByDateOccurred);
        return newData;
      };

      await queryClient.setQueryData(GamePlayTestsCacheKey(gameId), appendPlayTest);
      await queryClient.setQueryData(PlayTestCacheKey(gameId, data.id), data);

      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestId} playTestId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdatePlayTest = (gameId, playTestId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdatePlayTest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => updatePlayTest(token, gameId, playTestId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PlayTest} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }
      if (!playTestId) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PlayTest[]} current
       */
      const appendPlayTest = (current) => {
        const newData = [...current.filter((pt) => pt.id !== playTestId), data];
        newData.sort(ByDateOccurred);
        return newData;
      };

      await queryClient.setQueryData(GamePlayTestsCacheKey(gameId), appendPlayTest);
      await queryClient.setQueryData(PlayTestCacheKey(gameId, playTestId), data);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {(data: import('@seabrookstudios/pitch2table-core').PlayTest) => void} [onSuccess]
 * @param {Function} [onError]
 */
const useImportPlayTest = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').ImportPlayTest} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => importPlayTest(token, gameId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PlayTest} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PlayTest[]} current
       */
      const appendPlayTest = (current) => {
        const newData = [...current, data];
        newData.sort(ByDateOccurred);
        return newData;
      };

      await queryClient.setQueryData(GamePlayTestsCacheKey(gameId), appendPlayTest);

      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestId} playTestId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useDeletePlayTest = (gameId, playTestId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    mutationFn: async () => {
      const data = await wrapper(() => deletePlayTest(token, gameId, playTestId));
      return data;
    },
    onSuccess: async () => {
      if (!gameId) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: GamePlayTestsCacheKey(gameId) });

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 */
const useGamePlayTestMetaData = (gameId) => {
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useQuery({
    queryKey: GamePlayTestMetadataCacheKey(gameId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent[]>}
     */
    queryFn: async () => {
      /**
       * @type {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent[]}
       */
      const data = await wrapper(() => fetchGamePlayTestMetaData(token, gameId));

      data.sort(byOrder);

      return data;
    },
    staleTime: OneDay,
    enabled: hasToken(token),
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponentId} metaId
 */
const useGamePlayTestMetaDataEntry = (gameId, metaId) => {
  const { data: metaData = [] } = useGamePlayTestMetaData(gameId);

  return useQuery({
    queryKey: PlayTestMetaDataCacheKey(gameId, metaId),
    /**
     * @returns {Promise<import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent|null>}
     */
    queryFn: async () => {
      const topic = metaData.find((d) => d.id === metaId);
      return topic || null;
    },
    staleTime: OneMinute,
    enabled: metaData.length > 0,
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useCreatePlayTestMetaData = (gameId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').CreatePlayTestMetadata} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => createPlayTestMetaData(token, gameId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent[]} current
       */
      const appendMetaData = (current) => {
        const newData = [...current, data];
        newData.sort(byOrder);
        return newData;
      };

      await queryClient.setQueryData(GamePlayTestMetadataCacheKey(gameId), appendMetaData);
      await queryClient.setQueryData(PlayTestMetaDataCacheKey(gameId, data.id), data);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

/**
 * @param {import('@seabrookstudios/types').GameId} gameId
 * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponentId} metaId
 * @param {Function} [onSuccess]
 * @param {Function} [onError]
 */
const useUpdatePlayTestMetaData = (gameId, metaId, onSuccess, onError) => {
  const queryClient = useQueryClient();
  const token = useContext(CachedTokenContext);
  const wrapper = useInvalidationWrapper();

  return useMutation({
    /**
     * @param {import('@seabrookstudios/pitch2table-core').UpdatePlayTestMetadata} request
     */
    mutationFn: async (request) => {
      const data = await wrapper(() => updatePlayTestMetaData(token, gameId, metaId, request));
      return data;
    },
    /**
     * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent} data
     */
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!gameId) {
        return;
      }
      if (!metaId) {
        return;
      }

      /**
       * @param {import('@seabrookstudios/pitch2table-core').PlayTestMetadataComponent[]} current
       */
      const appendMetaData = (current) => {
        const newData = [...current.filter((m) => m.id !== metaId), data];
        newData.sort(byOrder);
        return newData;
      };

      await queryClient.setQueryData(GamePlayTestMetadataCacheKey(gameId), appendMetaData);
      await queryClient.setQueryData(PlayTestMetaDataCacheKey(gameId, metaId), data);

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
  });
};

export { ContractUrls, DesignerPublisherGameKey, DesignerPublisherGamesForGame, GameById, GameContracts, GamePlayTestMetadataCacheKey, GamePlayTestsCacheKey, GameRules, GameSellsheets, GameTopicsCacheKey, MyDocument, MyDocuments, MyEntitlements, MyGames, MyPublisherContacts, MyPublisherDocuments, MyPublishers, MyUnlinkedPublisher, MyUnlinkedPublishers, NonGameContractUrls, PlayTestCacheKey, PlayTestMetaDataCacheKey, Publisher, PublisherGameKey, PublisherGameUpdatesKey, TopicCacheKey, TopicMessagesCacheKey, useAcceptInvite, useAddGameToPublisher, useAddPublisherGameUpdate, useAddPublisherGameUpdateV, useAddPublisherGameUpdateV2, useAllRoyalties, useAllRoyaltiesByGame, useAllRoyaltiesByPublisher, useAllRoyaltiesByType, useAllRoyaltiesByYear, useAllRoyaltiesForPublisher, useAllRoyaltiesForType, useAllRoyaltiesForYear, useContact, useContacts, useCreateGameTopic, useCreatePlayTest, useCreatePlayTestMetaData, useCreateRoyaltyEvent, useCreateTopicMessage, useCreateUnlinkedPublisher, useDeclineInvite, useDeleteFollowUp, useDeletePlayTest, useDeletePublisherGame, useDeletePublisherGameUpdate, useDeleteRoyaltyEvent, useDocument, useFindByEmail, useGame, useGameContract, useGameContracts, useGamePlayTest, useGamePlayTestMetaData, useGamePlayTestMetaDataEntry, useGamePlayTests, useGamePublishers, useGameRoyalties, useGameRules, useGameSellsheets, useGameTopic, useGameTopics, useImportPlayTest, useInviteDetails, useInviteUser, useInvitesForGame, useInvitesForUser, useMyContactsRelatingToPublisher, useMyDesignerPublisherRelationship, useMyDocuments, useMyDocumentsRelatingToPublisher, useMyEntitlements, useMyGames, useMyProfile, useMyPublishers, useMySubmissionsToPublisher, useMyUnlinkedPublishers, useNonGameContract, usePublisherAndContacts, usePublisherGame, usePublisherGameAsDesigner, usePublisherGameAsPublisher, usePublisherGameUpdate, usePublisherGameUpdates, useRemoveUser, useSaveGame, useShortlistPublisher, useTopicMessages, useUnlinkedPublisher, useUpdateContract, useUpdateFollowUp, useUpdateGame, useUpdateGameTopic, useUpdateGameV2, useUpdateNonGameContract, useUpdatePlayTest, useUpdatePlayTestMetaData, useUpdatePreferredCurrency, useUpdateProfile, useUpdatePublisherGame, useUpdatePublisherGameUpdate, useUpdatePublisherGamev2, useUpdateRoyaltyEvent, useUpdateTopicMessage, useUserCurrencyFormatter, useWithdrawInvite };
